/**
 * chromeV2 — runtime feature flag for the Phase-2 platform chrome.
 *
 * Phase 2 recomposes the universal page chrome (banner → sticky FilterBar
 * card → Ask Avium card) into ONE sticky utility row: brand + page title,
 * compact filters with priority/overflow, an Ask Avium ⌘K launcher, and the
 * page's bannerRight tools. Approved via the design-review mockups
 * (2026-07-11); rollout + rollback runbook: docs/chrome-v2-rollout.md.
 *
 * Resolution order (first hit wins):
 *   1. localStorage override `avium:chrome-v2` — '1' forces ON, '0' forces
 *      OFF. Set per-browser via the URL: `?chrome=v2` / `?chrome=v1`.
 *      This is the instant, no-deploy rollback lever for a tester.
 *   2. Build-time default `VITE_CHROME_V2=1` — set on the Railway STAGING
 *      service only, so staging runs v2 while production stays v1 until the
 *      prod promotion is explicitly approved.
 *   3. Off. The v1 chrome is the fallback at every level — rollback is
 *      "stop opting in", never a code change.
 */

const LS_KEY = 'avium:chrome-v2';

/**
 * Apply a `?chrome=v2` / `?chrome=v1` URL override into localStorage so the
 * choice sticks across navigation. Exported for unit testing; called lazily
 * from isChromeV2() so no app-entry wiring is needed.
 */
export function applyChromeV2UrlOverride(search?: string): void {
  try {
    const qs = search ?? window.location.search;
    const v = new URLSearchParams(qs).get('chrome');
    if (v === 'v2') localStorage.setItem(LS_KEY, '1');
    else if (v === 'v1') localStorage.setItem(LS_KEY, '0');
  } catch {
    /* no window/localStorage (tests, SSR) — leave the stored value alone */
  }
}

let urlApplied = false;

export function isChromeV2(): boolean {
  if (!urlApplied) {
    urlApplied = true;
    applyChromeV2UrlOverride();
  }
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (ls === '1') return true;
    if (ls === '0') return false;
  } catch {
    /* localStorage unavailable — fall through to the build default */
  }
  return import.meta.env.VITE_CHROME_V2 === '1';
}

/** Test hook: reset the lazy URL-override latch. */
export function __resetChromeV2ForTests(): void {
  urlApplied = false;
}
